import { LabelValue } from '@/types/global';
import { useCallback } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';

interface UseChangeLabelValueWithSecondaryValueProps<
  FormValues extends FieldValues,
  T,
  U,
> {
  control: Control<FormValues>;
  primaryName: Path<FormValues>;
  secondaryName: Path<FormValues>;
  retrieveSecondary: (primaryValue: LabelValue<T>) => LabelValue<U> | undefined;
}

export const useChangeLabelValueWithSecondaryValue = <
  FormValues extends FieldValues,
  T,
  U,
>({
  control,
  primaryName,
  secondaryName,
  retrieveSecondary,
}: UseChangeLabelValueWithSecondaryValueProps<FormValues, T, U>) => {
  const {
    field: { onChange: onChangePrimary },
  } = useController({ control, name: primaryName });

  const {
    field: { onChange: onChangeSecondary },
  } = useController({ control, name: secondaryName });

  const handleChange = useCallback(
    (newValue: unknown) => {
      // react-select passes null when the field is cleared (isClearable)
      if (newValue == null) {
        onChangeSecondary({ value: '', label: '' });
        onChangePrimary(null);
        return;
      }

      const primaryValue = newValue as LabelValue<T>;
      const secondaryValue = retrieveSecondary(primaryValue);
      const secondary = secondaryValue ?? { value: '', label: '' };

      onChangeSecondary(secondary);
      onChangePrimary(primaryValue);
    },
    [onChangePrimary, onChangeSecondary, retrieveSecondary],
  );

  return handleChange;
};
